import { formatMoney } from "@theme/utilities";

/**
 * <hdt-installments form="{id}"> keeps the "10x de R$ X" / "R$ Y no Pix" lines
 * in sync with the selected variant.
 *
 * Liquid renders the first variant server-side; without this the block stayed
 * frozen on that price while <hdt-price> updated. Mirrors the theme's own
 * VariantChangeBase wiring (form attribute + 'variant:change').
 *
 * The math here mirrors snippets/installments.liquid — keep both in sync.
 */

const VARIANT_CHANGE = "variant:change";

const money = (cents) =>
  formatMoney(cents, window.themeHDN?.settings?.currencyFormat || "");

class Installments extends HTMLElement {
  constructor() {
    super();
    this._onVariantChanged = (event) => {
      const price = event.detail?.variant?.price;
      if (typeof price === "number") this.update(price);
    };
  }

  get form() {
    return (this._form ||= document.forms[this.getAttribute("form")]);
  }

  connectedCallback() {
    this.form?.addEventListener(VARIANT_CHANGE, this._onVariantChanged);
  }

  disconnectedCallback() {
    this.form?.removeEventListener(VARIANT_CHANGE, this._onVariantChanged);
  }

  update(cents) {
    const maxParcels = parseInt(this.dataset.maxParcels, 10) || 1;
    const minParcel = parseInt(this.dataset.minParcel, 10) || 1;
    const cashPercent = parseFloat(this.dataset.cashPercent) || 0;

    const parcels = Math.min(Math.floor(cents / minParcel), maxParcels);

    const parcelsEl = this.querySelector(".hdt-installments__parcels");
    if (parcelsEl) {
      const show = cents > 0 && parcels >= 2;
      parcelsEl.hidden = !show;
      if (show) {
        this.querySelector(".hdt-installments__count").textContent = `${parcels}x`;
        this.querySelector(".hdt-installments__value").textContent = money(
          Math.round(cents / parcels)
        );
      }
    }

    const cashEl = this.querySelector(".hdt-installments__cash");
    if (cashEl) {
      cashEl.hidden = cents <= 0;
      if (!cashEl.hidden) {
        this.querySelector(".hdt-installments__cash-value").textContent = money(
          cents - Math.round((cents * cashPercent) / 100)
        );
      }
    }

    document.dispatchEvent(new CustomEvent("currency:update"));
  }
}

if (!customElements.get("hdt-installments")) {
  customElements.define("hdt-installments", Installments);
}
